<script setup lang="ts">
import type { PaAPI } from '@rialtic/types'

type ExclusionConnectorStatus =
  | PaAPI.Schemas['ExclusionVersionConnector']['status']
  | 'Running'
  | 'Stopped'

const props = defineProps<{
  status?: ExclusionConnectorStatus | ExclusionConnectorStatus[]
  initials?: boolean
}>()

const statusOrder: Record<string, number> = {
  Running: 0,
  Stopped: 1,
  Draft: 2,
  Archived: 3,
}

const statuses = computed<ExclusionConnectorStatus[]>(() => {
  if (!props.status) return ['Stopped']
  const arr = Array.isArray(props.status) ? props.status : [props.status]
  return [...new Set(arr)].sort(
    (a, b) => (statusOrder[a] ?? 99) - (statusOrder[b] ?? 99),
  )
})

const statusLabel = (status: ExclusionConnectorStatus) =>
  status === 'Stopped' ? 'Not Running' : status

const statusInitial = (status: ExclusionConnectorStatus) => {
  switch (status) {
    case 'Running':
      return 'R'
    case 'Stopped':
      return 'N'
    case 'Draft':
      return 'Draft'
    case 'Archived':
      return 'A'
  }
}

const badgeClassesFor = (status: ExclusionConnectorStatus) => ({
  'bg-tertiary-700 text-white': status === 'Running',
  'bg-neutral-300 text-neutral-900': status === 'Stopped',
  'bg-neutral-100 text-neutral-800': status === 'Archived',
  'bg-primary-100 text-primary-600': status === 'Draft',
})
</script>

<template>
  <div v-if="statuses.length" class="flex items-center gap-1">
    <span
      v-for="status in statuses"
      :key="status"
      class="inline-flex items-center justify-center rounded-full text-xs"
      :class="[
        badgeClassesFor(status),
        initials && status !== 'Draft' ? 'h-5 w-5 p-0' : 'px-2.5 py-0.5',
      ]"
    >
      {{ initials ? statusInitial(status) : statusLabel(status) }}
    </span>
  </div>
</template>
